import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

/**
 * Read-only projection of a `Resolution` record scoped to the loan-enum
 * domains (`LoanType` and `RepaymentPlan`).
 *
 * Powers the admin "Loan Resolution Review Queue" — the cross-person
 * counterpart to the per-loan badges. Backed by
 * `GET /api/v1/loan-resolutions` (Api::V1::LoanResolutionResource).
 *
 * Note: `resolvable` is polymorphic on the server (Enum rows for LoanType
 * / RepaymentPlan). We intentionally do NOT model it as a relationship
 * here — the queue only needs the type/id + label/name quartet to
 * display, not to hydrate. The server exposes `resolvableLabel` (friendly
 * description) and `resolvableName` (machine key) directly so consumers
 * don't have to dereference a polymorphic relationship.
 */
// Nullable attrs below mirror the server contract: a freshly-created
// `Resolution` may have no source assigned (background classifier hasn't
// run yet) and no admin action (queue entry awaiting review). The
// `resolvable*` quartet is null for unresolved rows where the matcher
// produced no candidate. Modelling these as nullable surfaces "no value"
// in the template instead of forcing a fake placeholder.
export default class LoanResolutionModel extends Model {
  @attr declare domain: string;
  @attr declare rawText: string;
  @attr declare resolutionSource: 'regex' | 'opensearch' | 'admin' | null;
  @attr declare adminAction: 'confirmed' | 'rejected' | null;
  @attr declare resolvableType: string | null;
  @attr declare resolvableId: string | null;
  @attr declare resolvableName: string | null;
  @attr declare resolvableLabel: string | null;
  @attr declare matchesCount: number;
  @attr declare createdAt: string;
  @attr declare updatedAt: string;

  get hasPendingReview() {
    // == null catches both an explicit null from the API and an omitted
    // attr (which Ember Data surfaces as undefined).
    return this.adminAction == null && this.resolutionSource === 'opensearch';
  }

  get isAdminConfirmed() {
    return this.adminAction === 'confirmed';
  }

  get isAdminRejected() {
    return this.adminAction === 'rejected';
  }

  [Type] = 'loan-resolution' as const;
}
